import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError, formatBranchMoney } from "@/lib/api";
import { openReceipt, emailReceipt } from "@/lib/receipt";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { collectAlerts } from "@/pages/staff/brand/GuestPreferences";
import ManagerApprovalDialog from "./orders/ManagerApprovalDialog";
import VoidDialog from "./orders/VoidDialog";
import RefundDialog from "./orders/RefundDialog";
import LineItemRefundDialog from "./orders/LineItemRefundDialog";
import AddItemsDialog from "./orders/AddItemsDialog";
import GiftCardActions from "./orders/GiftCardActions";
import NewOrderDialog from "./orders/NewOrderDialog";
import CancelledBookingPanel from "./orders/CancelledBookingPanel";
import { StatusPill, PaymentStatusPill } from "./orders/StatusPill";
import TreatmentDateEditor from "./orders/TreatmentDateEditor";
import LoyaltyPointsPanel from "./orders/LoyaltyPointsPanel";
import ApplySpecialDialog from "./orders/ApplySpecialDialog";
import OrderItemsCard from "./orders/OrderItemsCard";
import PaymentSection from "./orders/PaymentSection";
import OrderHeaderCard from "./orders/OrderHeaderCard";
import { OrderHistoryPanel, HistoryDialog } from "./orders/OrderHistoryPanel";
import OrderNotesCard from "./orders/OrderNotesCard";
import { fmtDateTime, fmtDate } from "./orders/dateFmt";
import { useAuth } from "@/lib/auth";
import { isOwnerOrAdmin } from "@/lib/AuthEngine";
import { hasPermission } from "@/lib/permissions";
import {
  History, Printer, X, Mail, RefreshCcw, PackagePlus,
} from "lucide-react";
// IMMUTABLE clinical/financial brain — every Package ID, bundle, pro-rata,
// and shredding-prevention rule lives in PackageEngine.jsx. Do NOT inline
// package math here — see SYSTEM_RULES.md · "Package Engine Isolation".
import { buildOrderItemsForAppointment } from "@/lib/PackageEngine";
// IMMUTABLE Care Team roster brain — every appointment-date-aware Care
// Team resolution + historical-assignment merge lives in RosterEngine.jsx.
// Do NOT inline this logic here — see SYSTEM_RULES.md · "Roster Engine
// Isolation".
import { resolveOrderAppointmentDate, mergeStaffForOrder } from "@/lib/RosterEngine";

// Payment category → tab label. Kept in lockstep with backend PAYMENT_CATEGORIES
// (models.py) and BranchSettings PAYMENT_CATEGORIES.
const PAYMENT_TABS = [
  { value: "payment_on_file",  label: "Payment On File" },
  { value: "credit_card",      label: "Credit Card" },
  { value: "online",           label: "Online" },
  { value: "marketing",        label: "Marketing" },
  { value: "cash_equivalent",  label: "Cash / Equivalent" },
  { value: "account",          label: "Account" },
  { value: "other",            label: "More" },
];

// Providers for the Online payment tab. The dropdown value is passed straight
// to the backend and persisted on the payment record for reconciliation
// against Stripe / PayPal payouts on the Reports page.
const ONLINE_PROVIDERS = [
  { value: "stripe", label: "Stripe" },
  { value: "paypal", label: "PayPal" },
];
const ONLINE_PROVIDER_LABEL = Object.fromEntries(ONLINE_PROVIDERS.map((p) => [p.value, p.label]));

export default function BranchOrders() {
  const { bid } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const focusOrderId = params.get("order");
  const focusApptId = params.get("appt");
  // "Take Deposit" from the calendar right-click sets `mode=deposit`
  // in the URL — that entry point auto-prints the deposit receipt on
  // every payment save. The regular "Take Payment / Checkout" flow
  // suppresses per-payment printing and only fires on Close Order.
  const depositMode = params.get("mode") === "deposit";
  const [branch, setBranch] = useState(null);
  const [tab, setTab] = useState(focusOrderId || focusApptId ? "detail" : "manage");
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [series, setSeries] = useState([]);
  const [packages, setPackages] = useState([]);
  const [orders, setOrders] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [specials, setSpecials] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [giftTemplates, setGiftTemplates] = useState([]);

  // Order currently open in the detail view.
  const [order, setOrder] = useState(null);
  const [prefilledAppt, setPrefilledAppt] = useState(null);
  // A CANCELLED / NO-SHOW booking opened via `?appt=…` renders read-only —
  // the visit never happened so no draft order may be seeded for it.
  const [cancelledAppt, setCancelledAppt] = useState(null);
  // Iter-317 · Delayed Order Creation. `pendingSale` captures the
  // customer picked in the New Order dialog WITHOUT touching the
  // database — no order record, no `ORD-…` number, nothing written.
  // The DB row is created on the FIRST item-add via
  // `commitPendingSaleWithItem`. Closing the tab or navigating away
  // while pending discards the sale entirely.
  //   Shape: { customer: <profile|null>, isWalkIn: bool }
  //   Guarded from the URL (no `?order=…` param) so a refresh drops
  //   the pending state cleanly — no orphan draft ever exists.
  const [pendingSale, setPendingSale] = useState(null);
  const [committing, setCommitting] = useState(false);
  // Synchronous single-flight guard for the invoice-seeding effect below.
  // `prefilledAppt` is only set AFTER `POST /orders` resolves, so under
  // React.StrictMode (or when `services`/`packages` finish loading during
  // the async work) the effect can re-enter and create a duplicate draft
  // for the same appointment. This ref is written the instant the effect
  // starts a seed for a given appointment id, preventing a second run.
  const seedingApptRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [b, p, s, o, sr, pk, pt, sp, st, gt] = await Promise.all([
        staffApi.get("/branches"),
        staffApi.get("/products"),
        staffApi.get("/services", { params: { branch_id: bid } }),
        staffApi.get("/orders", { params: { branch_id: bid } }),
        staffApi.get("/series-templates", { params: { branch_id: bid, active_only: true } }).catch(() => ({ data: [] })),
        staffApi.get("/packages", { params: { branch_id: bid } }).catch(() => ({ data: [] })),
        staffApi.get(`/branches/${bid}/payment-types`, { params: { active_only: true } }).catch(() => ({ data: [] })),
        staffApi.get(`/branches/${bid}/specials`).catch(() => ({ data: [] })),
        staffApi.get(`/branches/${bid}/staff`).catch(() => ({ data: [] })),
        staffApi.get(`/branches/${bid}/gift-card-templates`).catch(() => ({ data: [] })),
      ]);
      setBranch(b.data.find((x) => x.id === bid) || null);
      setProducts(p.data);
      setServices(s.data);
      setOrders(o.data);
      setSeries(sr.data || []);
      setPackages(pk.data || []);
      setPaymentTypes(pt.data || []);
      setSpecials((sp.data || []).filter((x) => x.active !== false));
      setStaffList(st.data || []);
      setGiftTemplates((gt.data || []).filter((x) => x.active !== false));
    } catch (err) { toast.error(formatApiError(err)); }
  }, [bid]);
  useEffect(() => { load(); }, [load]);

  // Load a specific order into the detail view. If the order is still
  // open, silently reconcile any appointments that were linked AFTER
  // the draft was seeded so their line finally shows up on the bill.
  const loadOrder = useCallback(async (oid) => {
    try {
      const r = await staffApi.get(`/orders/${oid}`);
      let orderDoc = r.data;
      if (orderDoc && (orderDoc.status || "open") === "open") {
        try {
          const synced = await staffApi.post(`/orders/${oid}/sync-links`);
          if (synced.data) orderDoc = synced.data;
        } catch { /* silent — sync is best-effort */ }
      }
      setOrder(orderDoc);
      setTab("detail");
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);

  // React 18 StrictMode double-invokes effects in dev; without an
  // in-flight guard we'd fire two concurrent loadOrders whose returned
  // promises can arrive out of order, letting a stale response win.
  const loadingOrderRef = useRef(null);
  useEffect(() => {
    if (!focusOrderId) return;
    if (loadingOrderRef.current === focusOrderId) return;
    loadingOrderRef.current = focusOrderId;
    loadOrder(focusOrderId).finally(() => {
      if (loadingOrderRef.current === focusOrderId) loadingOrderRef.current = null;
    });
  }, [focusOrderId, loadOrder]);

  // From calendar "Take Payment/Check-Out": create a draft order pre-filled
  // with the appointment's service so the operator lands on a ready-made
  // invoice. When the appointment belongs to a package (shared
  // `link_group_id`), the draft is seeded with EVERY sibling appointment as
  // its own line item and an equal-share percent discount is applied so the
  // invoice total matches the package's configured price. Each line preserves
  // its own staff assignment for commission reporting.
  useEffect(() => {
    if (!focusApptId || services.length === 0) return;
    if (prefilledAppt === focusApptId) return;
    // Synchronously mark this appointment as in-flight. If another render
    // (StrictMode double-invoke, services/packages settling mid-await, etc.)
    // re-enters the effect for the SAME appt id before the POST resolves,
    // this early-return prevents a duplicate draft.
    if (seedingApptRef.current === focusApptId) return;
    seedingApptRef.current = focusApptId;
    // Deliberately NOT using a `cancelled` flag driven by the cleanup
    // function: under React.StrictMode (and any dep-change during the
    // await), cleanup fires while the async work is still mid-POST. If we
    // let cleanup abort setOrder/setParams, we end up with a persisted
    // draft on the server but a blank UI. Since `seedingApptRef` already
    // suppresses concurrent effects and `prefilledAppt === focusApptId`
    // suppresses subsequent runs, letting the async block always finish
    // is safe.
    (async () => {
      try {
        // STEP 0 — what kind of booking is this? A CANCELLED / NO-SHOW visit
        // never happened, so it must never seed a draft invoice. Read it
        // first (before any order probe that could reopen a voided bill) and
        // render the read-only cancellation record instead.
        const apptResp = await staffApi.get(`/appointments/${focusApptId}`);
        const appt = apptResp.data;
        const apptStatus = String(appt?.status || "").toLowerCase();
        if (apptStatus === "cancelled" || apptStatus === "no_show") {
          let covering = null;
          try {
            const probe = await staffApi.get(`/orders/by-appointment/${focusApptId}`, { params: { readonly: true } });
            covering = probe.data || null;
          } catch { /* 404 → no invoice covers this cancelled visit */ }
          if (covering) {
            // An invoice already carries this visit — open THAT order so the
            // original id + audit trail is what the operator sees.
            setOrder(covering);
            setCancelledAppt(null);
            setTab("detail");
            setPrefilledAppt(focusApptId);
            setParams({ order: covering.id }, { replace: true });
            return;
          }
          setCancelledAppt(appt);
          setOrder(null);
          setTab("detail");
          setPrefilledAppt(focusApptId);
          return;
        }
        // An order may ALREADY cover this appointment — an unpaid draft left
        // behind, or a bill that was settled (e.g. paid by series). Re-billing
        // the same visit created endless unpaid invoices and repeated series
        // redemptions, so reuse the existing invoice instead.
        try {
          const existing = await staffApi.get(`/orders/by-appointment/${focusApptId}`);
          if (existing.data) {
            let orderDoc = existing.data;
            if ((orderDoc.status || "open") === "open") {
              try {
                const synced = await staffApi.post(`/orders/${orderDoc.id}/sync-links`);
                if (synced.data) orderDoc = synced.data;
              } catch { /* silent */ }
            }
            setOrder(orderDoc);
            setTab("detail");
            setPrefilledAppt(focusApptId);
            setParams({ order: orderDoc.id }, { replace: true });
            if (orderDoc.status === "completed") {
              toast.info(`Invoice ${orderDoc.order_number} is already paid — showing the settled bill.`);
            } else if (orderDoc.status === "refunded") {
              toast.info(`Invoice ${orderDoc.order_number} was paid and refunded — showing the closed bill.`);
            }
            return;
          }
        } catch (probeErr) {
          // Only a real 404 means "nothing covers this appointment yet".
          // Auth/network/server failures must NOT fall through to creating a
          // duplicate draft — surface them and stop.
          if (probeErr?.response?.status !== 404) {
            seedingApptRef.current = null;
            toast.error(formatApiError(probeErr));
            return;
          }
        }

        // Fetch sibling appointments when this booking belongs to a link
        // group. The engine (PackageEngine.jsx) needs the full sibling
        // list to correctly split package children vs ad-hoc extras and
        // compute the pro-rata discount. Data-fetch stays here (it's
        // I/O); all pricing/bundling math is delegated to the engine.
        const linkGroupId = appt.link_group_id || null;
        let groupAppts = [];
        if (linkGroupId) {
          const groupResp = await staffApi.get("/appointments", {
            params: { branch_id: bid, link_group_id: linkGroupId, include_clipboard: true },
          });
          groupAppts = (groupResp.data || [])
            .filter((a) => a.status !== "cancelled")
            .sort((x, y) => String(x.start_iso).localeCompare(String(y.start_iso)));
        }
        const pkg = appt.package_id ? (packages.find((p) => p.id === appt.package_id) || null) : null;

        // Delegate to the immutable PackageEngine — DO NOT inline this
        // math anywhere else in the codebase (SYSTEM_RULES · Package
        // Engine Isolation).
        const { items, packageId, packageName, orderNote } = buildOrderItemsForAppointment({
          appt,
          groupAppts,
          pkg,
          services,
          branchId: bid,
        });

        // Create a fresh draft order for this appointment / package group.
        const created = await staffApi.post("/orders", {
          branch_id: bid,
          customer_id: appt.customer_id || null,
          customer_name: appt.customer_name || "Walk-in",
          items,
          payment_method: "unpaid",
          note: orderNote,
          package_id: packageId,
          package_name: packageName,
        });
        setOrder(created.data);
        setTab("detail");
        setPrefilledAppt(focusApptId);
        // Replace the URL to reference the new order rather than the appt so a
        // refresh loads the draft rather than re-creating another one.
        setParams({ order: created.data.id }, { replace: true });
      } catch (err) {
        // Release the single-flight guard so the operator can retry after
        // a transient failure. The successful branch above updates
        // `prefilledAppt` which keeps the effect from re-running anyway.
        seedingApptRef.current = null;
        toast.error(formatApiError(err));
      }
    })();
  }, [focusApptId, services, packages, bid, prefilledAppt, setParams]);

  const refreshOrder = async () => {
    if (!order) return;
    const r = await staffApi.get(`/orders/${order.id}`);
    setOrder(r.data);
    // Keep the Manage Orders list in sync so a tab-switch shows the latest state.
    try {
      const list = await staffApi.get("/orders", { params: { branch_id: bid } });
      setOrders(list.data);
    } catch { /* non-blocking */ }
    return r.data;
  };

  // Iter-241 CRITICAL FIX: refresh the currently-open order the instant
  // any linked appointment mutates (move / resize / specialist / status).
  // Without this the OrderHistoryPanel's `refreshKey` (derived from
  // `order.updated_at` and `order.events.length`) stays frozen at the
  // pre-edit snapshot, so freshly-written audit rows appear only after
  // a manual page reload. The subscription is keyed off the open
  // order id so unrelated appointment traffic is ignored.
  const orderRef = useRef(null);
  useEffect(() => { orderRef.current = order; }, [order]);
  useEffect(() => {
    if (!order?.id) return;
    // eslint-disable-next-line global-require
    const live = require("@/lib/liveSync");
    live.ensureOpen();
    const bump = () => {
      const cur = orderRef.current;
      if (!cur?.id) return;
      staffApi.get(`/orders/${cur.id}`).then((r) => setOrder(r.data)).catch(() => {});
    };
    const offAppt = live.subscribe("appointment_updated", bump);
    const offOrder = live.subscribe("order_updated", bump);
    return () => { offAppt(); offOrder(); };
  }, [order?.id]);

  const startNewOrder = () => setNewOrderOpen(true);
  const [newOrderOpen, setNewOrderOpen] = useState(false);

  // Iter-317 · Delayed Order Creation — helpers.
  // `buildOrderBody` centralises the POST /orders payload shape so
  // both the legacy prefill-from-appointment flow (line ~258) and
  // the new pending-sale commit flow speak the same vocabulary.
  const buildOrderBody = (customer, items = []) => (
    customer
      ? {
          branch_id: bid,
          customer_id: customer.id,
          customer_name: customer.full_name
            || `${customer.first_name || ""} ${customer.last_name || ""}`.trim()
            || customer.email
            || "Guest",
          items,
          payment_method: "unpaid",
        }
      : { branch_id: bid, customer_name: "Walk-in", items, payment_method: "unpaid" }
  );

  // Called from `NewOrderDialog::onStart`. Stages the sale in memory,
  // switches to the Checkout tab, closes the dialog. NO database
  // write — the record is materialised in `commitPendingSaleWithItem`
  // once the operator adds the first item.
  const createOrderFor = (customer) => {
    setPendingSale({ customer: customer || null, isWalkIn: !customer });
    setOrder(null);
    setPrefilledAppt(null);
    setTab("detail");
    setNewOrderOpen(false);
    // Iter-317 · Live state refresh (SYSTEM_RULES §3b) — pull the
    // latest orders list so the Manage tab remains in sync while the
    // operator lingers in pending-sale mode.
    (async () => {
      try {
        const list = await staffApi.get("/orders", { params: { branch_id: bid } });
        setOrders(list.data);
      } catch { /* non-blocking */ }
    })();
  };

  // Materialise the pending sale by POSTing the FIRST item. Server
  // mints the `ORD-…` number and returns the persisted document.
  // Only called from the pending-sale panel's Add Items handler.
  const commitPendingSaleWithItem = async (payload) => {
    if (!pendingSale || committing) return;
    setCommitting(true);
    try {
      const body = buildOrderBody(pendingSale.customer, [payload]);
      const created = await staffApi.post("/orders", body);
      setOrder(created.data);
      setPendingSale(null);
      setParams({ order: created.data.id }, { replace: true });
      toast.success(`Sale registered — ${created.data.order_number || created.data.id.slice(0, 8)}`);
      try {
        const list = await staffApi.get("/orders", { params: { branch_id: bid } });
        setOrders(list.data);
      } catch { /* non-blocking */ }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCommitting(false);
    }
  };

  // Discard the pending sale without any DB round-trip.
  const discardPendingSale = () => {
    setPendingSale(null);
    setTab("manage");
  };

  return (
    <StaffShell branchName={branch?.name} context="branch">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Orders / POS</h1>
        <Button onClick={startNewOrder} variant="outline" size="sm" data-testid="new-order-btn">
          + New Order
        </Button>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="detail" data-testid="tab-detail" disabled={!order && !pendingSale && !cancelledAppt}>Checkout</TabsTrigger>
          <TabsTrigger value="manage" data-testid="tab-manage-orders">Manage Orders</TabsTrigger>
        </TabsList>

        <TabsContent value="detail" className="mt-4">
          {order ? (
            <OrderDetail
              order={order}
              branch={branch}
              products={products}
              services={services}
              series={series}
              giftTemplates={giftTemplates}
              packages={packages}
              paymentTypes={paymentTypes}
              specials={specials}
              staffList={staffList}
              refresh={refreshOrder}
              depositMode={depositMode}
              onClose={() => { setOrder(null); setTab("manage"); setParams({}); load(); }}
            />
          ) : cancelledAppt ? (
            <CancelledBookingPanel
              appt={cancelledAppt}
              branchId={bid}
              order={null}
              onBack={() => { setCancelledAppt(null); setPrefilledAppt(null); setTab("manage"); setParams({}); }}
            />
          ) : pendingSale ? (
            <PendingSalePanel
              pendingSale={pendingSale}
              branch={branch}
              products={products}
              services={services}
              series={series}
              giftTemplates={giftTemplates}
              packages={packages}
              staffList={staffList}
              committing={committing}
              onAddFirstItem={commitPendingSaleWithItem}
              onDiscard={discardPendingSale}
            />
          ) : (
            <Card className="rounded-lg"><CardContent className="p-8 text-center text-sm text-muted-foreground">
              No order open. Click <span className="font-medium text-foreground">New Order</span> to start one, or open an existing order from the Manage tab.
            </CardContent></Card>
          )}
        </TabsContent>

        <TabsContent value="manage" className="mt-4">
          <Card className="rounded-lg">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Order #</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Treatment</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.length === 0 ? (
                    <TableRow><TableCell colSpan={9} className="text-center text-sm text-muted-foreground py-8">No orders yet.</TableCell></TableRow>
                  ) : orders.map((o) => (
                    <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
                      <TableCell className="font-mono text-xs">{o.order_number || o.id.slice(0, 8)}</TableCell>
                      <TableCell className="text-xs">{fmtDateTime(o.created_at)}</TableCell>
                      <TableCell className="text-xs" data-testid={`order-treatment-date-${o.id}`}>
                        {o.treatment_date ? fmtDate(o.treatment_date) : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>{o.customer_name}</TableCell>
                      <TableCell className="text-xs">{(o.items || []).length}</TableCell>
                      <TableCell><PaymentStatusPill order={o} /></TableCell>
                      <TableCell className="text-right">{formatBranchMoney(branch, o.total)}</TableCell>
                      <TableCell className="text-right">{formatBranchMoney(branch, Math.max(0, (o.total || 0) - (o.amount_paid || 0)))}</TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button size="sm" variant="outline" onClick={() => { navigate(`/staff/branch/${bid}/orders?order=${o.id}`); loadOrder(o.id); }} data-testid={`order-open-${o.id}`}>Open</Button>
                        {(o.status === "open" || o.status === "completed") && (
                          <Button size="sm" variant="ghost" onClick={() => { navigate(`/staff/branch/${bid}/orders?order=${o.id}`); loadOrder(o.id); }} data-testid={`order-manage-${o.id}`}>Manage</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <NewOrderDialog open={newOrderOpen} onOpenChange={setNewOrderOpen} onStart={createOrderFor} branchId={bid} />
    </StaffShell>
  );
}


/* ==================================================================
   PendingSalePanel — Iter-317 · Delayed Order Creation.
   Renders the Checkout tab BEFORE the order exists in the database.
   Shows the resolved guest, hides the Order # (there isn't one
   yet), and offers a single "Add first item" call-to-action. The
   moment the operator picks the first line item, the parent's
   `onAddFirstItem` handler POSTs `/orders` with that item — the
   server mints the `ORD-…` number and the panel gives way to the
   full OrderDetail on the next render. If the operator closes the
   tab or discards the sale, no DB row ever exists.
   ================================================================== */
function PendingSalePanel({
  pendingSale, branch, products, services, series, giftTemplates,
  packages, staffList, committing, onAddFirstItem, onDiscard,
}) {
  const [addItemsOpen, setAddItemsOpen] = useState(false);
  const guest = pendingSale?.customer;
  const guestName = guest
    ? (guest.full_name
        || `${guest.first_name || ""} ${guest.last_name || ""}`.trim()
        || guest.email
        || "Guest")
    : "Walk-in";
  const guestSub = guest
    ? [guest.email, guest.whatsapp].filter(Boolean).join(" · ")
    : "Anonymous counter sale";
  return (
    <>
      <Card className="rounded-lg" data-testid="pending-sale-panel">
        <CardContent className="p-6 space-y-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-1">
                New Sale · not yet saved
              </div>
              <h2 className="text-xl font-semibold truncate" data-testid="pending-sale-guest-name">
                {guestName}
              </h2>
              {guestSub && (
                <p className="text-xs text-muted-foreground mt-0.5 truncate">
                  {guestSub}
                </p>
              )}
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-1">
                Order&nbsp;#
              </div>
              <div
                className="font-mono text-sm text-muted-foreground italic"
                data-testid="pending-sale-order-number"
                title="Generated once the first item is added"
              >
                — pending —
              </div>
            </div>
          </div>

          <div className="rounded-md border border-dashed border-border bg-muted/30 p-5 text-center space-y-3" data-testid="pending-sale-hint">
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              This sale hasn&rsquo;t been saved yet. The Order&nbsp;# and
              database record are created the moment you add the first item.
              Close the tab or discard to walk away with nothing written.
            </p>
            <Button
              size="lg"
              onClick={() => setAddItemsOpen(true)}
              disabled={committing}
              data-testid="pending-sale-add-first-item-btn"
            >
              <PackagePlus className="w-4 h-4 mr-2" />
              {committing ? "Registering…" : "Add first item"}
            </Button>
          </div>

          <div className="flex items-center justify-between pt-1">
            <p className="text-[11px] text-muted-foreground">
              Branch · <span className="font-medium text-foreground">{branch?.name || "—"}</span>
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={onDiscard}
              disabled={committing}
              data-testid="pending-sale-discard-btn"
              className="text-muted-foreground"
            >
              Discard sale
            </Button>
          </div>
        </CardContent>
      </Card>

      <AddItemsDialog
        open={addItemsOpen}
        onOpenChange={setAddItemsOpen}
        products={products}
        services={services}
        series={series}
        giftTemplates={giftTemplates}
        packages={packages}
        branch={branch}
        staffList={staffList}
        onAdd={async (payload) => {
          setAddItemsOpen(false);
          await onAddFirstItem(payload);
        }}
      />
    </>
  );
}


/* ==================================================================
   OrderDetail — the mockup layout: header card, order-items table,
   payment tabs. Handles all mutating actions and refreshes the order
   after each server round-trip.
   ================================================================== */
function OrderDetail({ order, branch, products, services, series, giftTemplates, packages, paymentTypes, specials, staffList, refresh, onClose, depositMode = false }) {
  const { staff } = useAuth();
  const canAuthoriseFinance = hasPermission(staff, "location.orders_pos.delete");
  const canEditPos = hasPermission(staff, "location.orders_pos.edit");
  const [addItemsOpen, setAddItemsOpen] = useState(false);
  const [applySpecialOpen, setApplySpecialOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [editingItemIdx, setEditingItemIdx] = useState(null);
  const [scan, setScan] = useState("");
  const [customerDetail, setCustomerDetail] = useState(null);
  const [paymentAlert, setPaymentAlert] = useState(null);   // {name, text} once per customer per session
  const [voidOpen, setVoidOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  // Line-item refund state (past-day only). `lineRefundIdx` is the item
  // index the operator picked; the dialog gathers payout method + note
  // and either hits the endpoint directly (delete-holding user) or hands
  // off to the Manager Approval popup (edit-only user).
  const [lineRefundIdx, setLineRefundIdx] = useState(null);
  // Fix 1 (Iter-f10): per-payment refund confirmation. The X delete
  // icon in Recorded Payments only appears for payments stamped on
  // today's branch calendar date; any older payment surfaces a
  // Refund button instead that opens this confirmation dialog before
  // calling `/orders/{oid}/refund` with the payment's own amount.
  const [refundPayment, setRefundPayment] = useState(null);
  const [refundingPayment, setRefundingPayment] = useState(false);
  // Iter-f11 — Write Off dialog state. Owner / Admin only. A pending
  // deposit that will never be converted (no-show, abandoned package)
  // is written off with a mandatory reason; the payment line stays on
  // the invoice but the Sales-by-Payment report flips it out of
  // Treatment Sales forever.
  const [writeOffDeposit, setWriteOffDeposit] = useState(null);
  const [writeOffReason, setWriteOffReason] = useState("");
  const [writingOff, setWritingOff] = useState(false);
  // Owner / Admin only — gates both Refund and Write Off affordances
  // in the Recorded Payments list. Falls back to the historic refund
  // permission so brand admins keep working even if their role flag
  // changes.
  const canManageRefunds = isOwnerOrAdmin(staff) || hasPermission(staff, "financials.process_refunds_voids");
  // Iter-365 (Feb 2026): legacy `sendingInvite` + `activationLink`
  // state DELETED — the "Send / Resend Invite" affordance is gone.
  // Guests activate on their first OTP sign-in via /portal/login.
  const historyRef = useRef(null);
  const scrollToHistory = () => {
    historyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const reloadCustomerDetail = async () => {
    if (!order?.customer_id) return;
    try {
      const r = await staffApi.get(`/customers/${order.customer_id}`);
      setCustomerDetail(r.data);
    } catch { /* silent */ }
  };

  // Live-sync (Feb 2026): whenever any surface (CheckInDialog, a peer
  // Order tab, staff loyalty adjustment) broadcasts a
  // `cocoon:customer-updated` event we refetch the customer detail so
  // the header's Member badge + loyalty balance always match the DB
  // source of truth. Ignore events for OTHER customers to avoid
  // unnecessary work.
  useEffect(() => {
    const onUpdated = (e) => {
      const cidUpdated = e?.detail?.customer_id;
      if (!cidUpdated || cidUpdated === order?.customer_id) {
        reloadCustomerDetail();
      }
    };
    window.addEventListener("cocoon:customer-updated", onUpdated);
    return () => window.removeEventListener("cocoon:customer-updated", onUpdated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.customer_id]);

  // Refetch when the browser tab regains focus — protects against a
  // stale loyalty balance when the operator was working on a peer
  // terminal that mutated the same guest.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") reloadCustomerDetail();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.customer_id]);

  // Iter-365 (Feb 2026): `sendMembershipInvitation` handler DELETED.
  // The backend `POST /customers/{cid}/send-activation` endpoint now
  // returns 410 Gone. Guests become Members on first OTP sign-in.

  // Branch active business date — the source of truth for the Void vs
  // Refund decision. `undefined` = still loading; `null` = never
  // confirmed (falls back to bali_date comparison); string = confirmed
  // ISO date. Distinguishing "loading" from "never confirmed" is what
  // stops the button from flickering Void → Refund for a stale order on
  // first render (iteration 106).
  const [activeBusinessDate, setActiveBusinessDate] = useState(undefined);
  useEffect(() => {
    if (!branch?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await staffApi.get(`/branches/${branch.id}/business-date`);
        if (!cancelled) setActiveBusinessDate(r.data?.active_business_date || null);
      } catch { if (!cancelled) setActiveBusinessDate(null); }
    })();
    return () => { cancelled = true; };
  }, [branch?.id, order.id, order.status, order.updated_at]);

  // Care Team Dispatch — Appointment-date-aware staff list.
  //
  // Bug: the Care Team dropdown on the Order · Items surface was pulling
  // `staffList` sourced from `GET /branches/{bid}/staff` with NO date
  // param — so it silently used today's roster. That collapsed a closed
  // order raised three weeks ago into "Unknown" whenever a specialist had
  // since left the clinic, and offered today's Care Team as if they had
  // treated the historical guest.
  //
  // Fix (surgical, engine-owned): resolve the order's Appointment Date via
  // `RosterEngine.resolveOrderAppointmentDate`, refetch the Care Team
  // scoped to that date, then merge in any historical `staff_ids` that
  // still ride on the order's line items via
  // `RosterEngine.mergeStaffForOrder`. The merge preserves every
  // pre-existing assignment (Rule 5 — State Integrity) and keeps names
  // visible even for staff whose profiles were deleted.
  //
  // Global to all 8 branches — the same date-driven pipeline runs on
  // every branch's Order page. No engine boundary is crossed
  // (BillingEngine / PackageEngine / CheckInEngine / AuthEngine /
  // ReportingEngine / NotificationEngine / ClientPortalEngine are all
  // untouched; only RosterEngine gained the two new pure helpers).
  const appointmentDate = useMemo(() => resolveOrderAppointmentDate(order), [order]);
  const [dateScopedStaff, setDateScopedStaff] = useState(null);
  useEffect(() => {
    if (!branch?.id) return;
    if (!appointmentDate) { setDateScopedStaff(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await staffApi.get(`/branches/${branch.id}/staff`, {
          params: { date: appointmentDate },
        });
        if (!cancelled) setDateScopedStaff(Array.isArray(r.data) ? r.data : null);
      } catch {
        if (!cancelled) setDateScopedStaff(null);
      }
    })();
    return () => { cancelled = true; };
  }, [branch?.id, appointmentDate]);

  const orderStaffList = useMemo(
    () => mergeStaffForOrder({
      dateScopedList: dateScopedStaff ?? staffList,
      fallbackList: staffList,
      order,
    }),
    [dateScopedStaff, staffList, order],
  );

  // Same-business-day = the order's stamped business_date matches the
  // branch's currently active one. Legacy orders (before this feature)
  // fall back to the "same Bali day" rule so pre-existing bills keep the
  // exact same behaviour they had before.
  const legacyToday = (() => {
    try { return new Intl.DateTimeFormat("en-CA", { timeZone: branch?.timezone || "Asia/Makassar", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date()); }
    catch { return new Date().toISOString().slice(0, 10); }
  })();
  // While the branch business-date query is still in flight AND the order
  // has a stamped business_date, we hold off on rendering either button
  // (a transient wrong-action would let the operator hit Void on an
  // already-closed day).
  const businessDateLoading = activeBusinessDate === undefined && !!order.business_date;
  const isSameBusinessDay = order.business_date && activeBusinessDate
    ? order.business_date === activeBusinessDate
    : (order.bali_date === legacyToday);

  // Whenever the customer_id changes, fetch a fuller profile so the header
  // can show phone + email like the Mindbody-style mockup.
  useEffect(() => {
    let cancelled = false;
    if (!order?.customer_id) { setCustomerDetail(null); return; }
    (async () => {
      try {
        const r = await staffApi.get(`/customers/${order.customer_id}`);
        if (cancelled) return;
        setCustomerDetail(r.data);
        // Payment alert popup — surface a staff-only warning the first time
        // this customer's order is opened in this browser tab.
        const c = r.data?.customer;
        const shownKey = `payment_alert_shown_${c?.id || ""}_${order.id}`;
        if (c && !sessionStorage.getItem(shownKey)) {
          const items = collectAlerts(c, "payment");
          if (items.length) {
            setPaymentAlert({ name: c.full_name || c.email || "customer", items });
            sessionStorage.setItem(shownKey, "1");
          }
        }
      } catch { if (!cancelled) setCustomerDetail(null); }
    })();
    return () => { cancelled = true; };
  }, [order?.customer_id, order?.id, order?.status, order?.loyalty_restored_at, order?.loyalty_points_used]);

  // `closed_no_show` behaves like a closed lifecycle status for the
  // duration of the no-show — Take Payment/Checkout is gated off,
  // items become read-only, loyalty redeem is locked. Reverting the
  // no-show flips the order back to "open" which wakes everything up.
  const isClosed = order.status === "voided" || order.status === "refunded" || order.status === "completed" || order.status === "merged" || order.status === "closed_no_show";

  // Payment tabs: only show tabs that actually have at least one payment type
  // configured for this branch (avoids empty tabs on lightly-configured sites).
  const activeCategories = useMemo(() => {
    const set = new Set(paymentTypes.map((p) => p.category || "other"));
    return PAYMENT_TABS.filter((t) => set.has(t.value));
  }, [paymentTypes]);
  const defaultPaymentTab = activeCategories[0]?.value || "cash_equivalent";
  const [paymentTab, setPaymentTab] = useState(defaultPaymentTab);
  useEffect(() => { if (activeCategories.length && !activeCategories.find((c) => c.value === paymentTab)) setPaymentTab(activeCategories[0].value); }, [activeCategories, paymentTab]);

  const [paymentTypeId, setPaymentTypeId] = useState("");
  const [paymentAmount, setPaymentAmount] = useState(String(Math.max(0, (order.total || 0) - (order.amount_paid || 0))));
  const [onlineProvider, setOnlineProvider] = useState("stripe");
  useEffect(() => { setPaymentAmount(String(Math.max(0, (order.total || 0) - (order.amount_paid || 0)))); }, [order.total, order.amount_paid]);
  // Iter-228 — Cash / Equivalent tab must default to "Cash" (then
  // "QRIS", then everything else in configured order). Alphabetical
  // ordering is deliberately AVOIDED so the operator never has to
  // pick Cash manually — the most-used method sits at the top and is
  // auto-selected the moment the tab opens.
  const CASH_PRIORITY_NAMES = ["cash", "qris"];
  const typesForTab = useMemo(() => {
    const scoped = paymentTypes.filter((p) => (p.category || "other") === paymentTab);
    if (paymentTab !== "cash_equivalent") return scoped;
    return [...scoped].sort((a, b) => {
      const ai = CASH_PRIORITY_NAMES.indexOf(String(a.name || "").trim().toLowerCase());
      const bi = CASH_PRIORITY_NAMES.indexOf(String(b.name || "").trim().toLowerCase());
      // Priority names sort first (Cash → 0, QRIS → 1); everything
      // else keeps its original configured position (offset by 1000
      // so it can never overtake a priority row).
      const av = ai === -1 ? 1000 + scoped.indexOf(a) : ai;
      const bv = bi === -1 ? 1000 + scoped.indexOf(b) : bi;
      return av - bv;
    });
  }, [paymentTypes, paymentTab]);
  // Reset the selected payment type whenever the tab flips OR the
  // ordered list changes so the auto-pick honours the new default.
  useEffect(() => { setPaymentTypeId(typesForTab[0]?.id || ""); }, [paymentTab, typesForTab]);
  // Reset provider back to the default whenever the operator flips away from
  // the Online tab so a fresh visit doesn't retain a stale selection.
  useEffect(() => { if (paymentTab !== "online") setOnlineProvider("stripe"); }, [paymentTab]);

  // ---- Gift voucher payment (number + Search validation) -------------------
  const selectedPaymentType = paymentTypes.find((p) => p.id === paymentTypeId);
  const isVoucherType = /gift|voucher/i.test(selectedPaymentType?.name || "");
  const [voucherCode, setVoucherCode] = useState("");
  const [voucherInfo, setVoucherInfo] = useState(null);
  const [voucherChecking, setVoucherChecking] = useState(false);
  useEffect(() => { setVoucherCode(""); setVoucherInfo(null); }, [paymentTypeId]);

  // ---- Series payment options (matching treatments on this bill) -----------
  const [seriesOptions, setSeriesOptions] = useState([]);
  const [seriesPanelOpen, setSeriesPanelOpen] = useState(true);
  useEffect(() => {
    let cancelled = false;
    if (!order?.customer_id || order.status !== "open") { setSeriesOptions([]); return; }
    (async () => {
      try {
        const r = await staffApi.get(`/orders/${order.id}/series-options`);
        if (!cancelled) setSeriesOptions(r.data || []);
      } catch { if (!cancelled) setSeriesOptions([]); }
    })();
    return () => { cancelled = true; };
  }, [order?.id, order?.status, order?.updated_at]);

  const total = order.total || 0;
  const paid = order.amount_paid || 0;
  // Derive the balance from the figures on screen instead of trusting the
  // persisted `balance_due`. A stale 0 on an unpaid bill used to hide the
  // whole payment form behind the "Fully paid" banner (ORD-6FB97C), which
  // made the invoice impossible to settle.
  const due = Math.max(0, total - paid);
  const progressPct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0;

  // ---- Actions ------------------------------------------------------------
  const addItem = async (payload) => {
    try {
      await staffApi.post(`/orders/${order.id}/items`, payload);
      await refresh();
      toast.success("Item added");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const removeItem = async (idx) => {
    try { await staffApi.delete(`/orders/${order.id}/items/${idx}`); await refresh(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  // Clear the invoice's staged loyalty redemption (X on the per-line
  // loyalty chip). Sends `points: 0` which zeroes the invoice's
  // loyalty_points_used AND triggers a full recalc — surcharge + PPN
  // rebuild against the original base immediately.
  const clearLoyaltyOnOrder = async () => {
    try {
      await staffApi.post(`/orders/${order.id}/loyalty/redeem`, { points: 0 });
      await refresh();
      toast.success("Loyalty redemption cleared");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const adjustItem = async (idx, adjustment) => {
    try { await staffApi.patch(`/orders/${order.id}/items/${idx}`, { adjustment }); await refresh(); return true; }
    catch (err) { toast.error(formatApiError(err)); return false; }
  };
  // Iter-377 · Price-override approval request. Staff with Edit (but
  // not Delete) on Order/POS cannot apply an override directly — this
  // posts to the Approvals queue, which paints a "Pending Approval"
  // badge on the line and in-app-notifies every Delete-permission
  // holder in the branch.
  const requestPriceOverride = async (idx, { new_price, reason }) => {
    try {
      await staffApi.post(`/pos-approvals`, {
        order_id: order.id,
        kind: "price_override",
        payload: { item_index: idx, new_price, reason: reason || "" },
        note: reason || "",
      });
      await refresh();
      toast.success("Price override sent for manager approval");
      return true;
    } catch (err) {
      toast.error(formatApiError(err));
      return false;
    }
  };
  const setItemStaff = async (idx, staff_ids) => {
    try { await staffApi.patch(`/orders/${order.id}/items/${idx}/staff`, { staff_ids }); await refresh(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const setItemQuantity = async (idx, quantity) => {
    try {
      await staffApi.patch(`/orders/${order.id}/items/${idx}/quantity`, { quantity });
      await refresh();
      return true;
    } catch (err) {
      toast.error(formatApiError(err));
      return false;
    }
  };
  const applyInvoiceSpecial = async (sid) => {
    try { await staffApi.post(`/orders/${order.id}/apply-special`, { special_id: sid }); await refresh(); setApplySpecialOpen(false); toast.success("Special applied"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const removeInvoiceSpecial = async () => {
    try { await staffApi.delete(`/orders/${order.id}/special`); await refresh(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const addPayment = async () => {
    const amt = parseFloat(paymentAmount);
    if (!paymentTypeId) return toast.error("Choose a payment type");
    if (!(amt > 0)) return toast.error("Amount must be > 0");
    if (isVoucherType && !voucherInfo?.valid) return toast.error("Search and validate the voucher number first");
    if (paymentTab === "online" && !onlineProvider) return toast.error("Choose an online provider (Stripe or PayPal)");
    try {
      const payload = { payment_type_id: paymentTypeId, amount: amt };
      if (isVoucherType && voucherInfo?.valid) payload.gift_card_code = voucherInfo.card.code;
      if (paymentTab === "online") payload.online_provider = onlineProvider;
      // Deposit vs Payment Separation (Feb 2026). Tag the payment as
      // a Deposit so it (a) flows straight into Sales reports as
      // cash-on-hand and (b) doesn't accidentally close the order.
      // The regular Take-Payment/Checkout flow leaves this False so
      // the payment only counts once the operator clicks Close Order.
      if (depositMode) payload.is_deposit = true;
      // 1. Persist first — every server-side recalculation (surcharges,
      //    tax, loyalty) must land in Mongo before we open a print
      //    window whose HTML is built from that same order doc.
      const resp = await staffApi.post(`/orders/${order.id}/payments`, payload);
      setVoucherCode(""); setVoucherInfo(null);
      // 2. Re-fetch the order so the header/summary cards paint the
      //    new balance immediately in the POS UI.
      await refresh();
      // 3. Auto-print — but ONLY when the operator entered via the
      //    calendar's "Take Deposit" shortcut (`depositMode`). The
      //    regular Take Payment / Checkout flow suppresses per-payment
      //    printing so the customer only ever receives one final
      //    invoice (fired from `closeOrder` below). Managers can
      //    still hit the manual Print button on the header any time.
      const saved = resp?.data || {};
      const newTotal = Number(saved.total ?? order.total ?? 0);
      const newPaid = Number(saved.amount_paid ?? 0);
      const fullyPaid = newTotal > 0 && newPaid >= newTotal - 0.01;
      if (depositMode) {
        openReceipt(order.id, { variant: "deposit" });
        // Fire-and-forget audit log so managers see the row in Order
        // History even though the printer is a client-side pop-up.
        staffApi.post(`/orders/${order.id}/receipt/log-print`, null, { params: { variant: "deposit" } }).catch(() => {});
        toast.success("Deposit recorded — printing deposit receipt");
      } else {
        toast.success(fullyPaid ? "Payment recorded — ready to Close Order" : "Payment recorded");
      }
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const searchVoucher = async () => {
    if (!voucherCode.trim()) return toast.error("Enter the voucher number");
    setVoucherChecking(true);
    try {
      const r = await staffApi.get("/gift-cards/lookup", { params: { code: voucherCode.trim() } });
      setVoucherInfo(r.data);
      if (r.data?.valid) setPaymentAmount(String(Math.min(Number(r.data.card.balance || 0), due)));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setVoucherChecking(false); }
  };
  const payWithSeries = async (opt) => {
    try {
      await staffApi.post(`/orders/${order.id}/pay-with-series`, { client_series_id: opt.client_series_id, item_index: opt.item_index });
      await refresh();
      toast.success(`Paid with series — ${Math.max(0, (opt.quantity_remaining || 1) - 1)} session(s) left`);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const removePayment = async (pid) => {
    try { await staffApi.delete(`/orders/${order.id}/payments/${pid}`); await refresh(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  // Fix 1 (Iter-f10) — refund a single past-day payment line. Maps
  // the payment's category to a legal `payout_type` so the refund
  // posts to the same channel it was received on; `online` /
  // `gift_card` / `account` all fall back to `cash` since the full
  // Refund Order dialog is the right place to pick a non-matching
  // payout. Order stays open; the refund is written to `order.refunds`
  // and the audit log, matching the manual refund flow.
  const handleRefundPayment = async () => {
    if (!refundPayment) return;
    const category = refundPayment.category || "other";
    const payoutMap = {
      cash_equivalent: "cash",
      credit_card:     "credit_card",
      online:          "online",
      marketing:       "cash",
      payment_on_file: "cash",
      account:         "cash",
      other:           "cash",
    };
    const payout = payoutMap[category] || "cash";
    setRefundingPayment(true);
    try {
      await staffApi.post(`/orders/${order.id}/refund`, {
        amount: Number(refundPayment.amount) || 0,
        payout_type: payout,
        // Iter-f11 — tag the specific deposit payment being refunded so
        // the backend can flip its `deposit_status` to `refunded` and
        // the Sales-by-Payment report keeps the money in the Deposit
        // sub-row (not Treatment Sales).
        source_payment_id: refundPayment.id,
        note: `Refund of ${refundPayment.payment_type_name || "payment"} dated ${(refundPayment.at || "").slice(0, 10)}`,
      });
      await refresh();
      toast.success("Payment refunded");
      setRefundPayment(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRefundingPayment(false);
    }
  };
  const closeOrder = async () => {
    try {
      // Wait for the backend to flip the order to `completed` before
      // firing the print window — the receipt HTML resolves the
      // `variant="final"` PAID badge off the persisted status.
      await staffApi.post(`/orders/${order.id}/close`);
      await refresh();
      // Auto-print the Tax Invoice with the PAID badge. Staff can
      // still hit the manual "Print Receipt" button on the header
      // for extra copies. Also stamp an audit row so the Order
      // History records the auto-print event.
      openReceipt(order.id, { variant: "final" });
      staffApi.post(`/orders/${order.id}/receipt/log-print`, null, { params: { variant: "final" } }).catch(() => {});
      toast.success("Order closed — printing final invoice");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const doScan = () => {
    if (!scan) return;
    // PRO Product Gating (Feb 2026): SKU scans at the checkout counter must
    // also refuse professional-use items so a PRO SKU cannot be smuggled in
    // via the barcode field even though it is hidden from the search list.
    const p = products.find((x) => x.sku === scan.trim() && !x.is_pro);
    if (!p) return toast.error("SKU not found");
    addItem({ kind: "product", ref_id: p.id, name: p.name, quantity: 1, unit_price: p.price, discount: 0 });
    setScan("");
  };

  return (
    <div className="space-y-4" data-testid="order-detail">
      {/* ------ Header card ------ */}
      <OrderHeaderCard
        order={order}
        branch={branch}
        customerDetail={customerDetail}
        onClose={onClose}
        refresh={refresh}
        scrollToHistory={scrollToHistory}
        total={total}
        paid={paid}
        due={due}
        progressPct={progressPct}
        businessDateLoading={businessDateLoading}
        isSameBusinessDay={isSameBusinessDay}
        setVoidOpen={setVoidOpen}
        setRefundOpen={setRefundOpen}
      />
      {/* ------ Order items ------ */}
      <OrderItemsCard
        order={order}
        branch={branch}
        staffList={orderStaffList}
        specials={specials}
        isClosed={isClosed}
        editingItemIdx={editingItemIdx}
        setEditingItemIdx={setEditingItemIdx}
        scan={scan}
        setScan={setScan}
        doScan={doScan}
        onAddItems={() => setAddItemsOpen(true)}
        onApplySpecial={() => setApplySpecialOpen(true)}
        onRemoveInvoiceSpecial={removeInvoiceSpecial}
        adjustItem={adjustItem}
        setItemStaff={setItemStaff}
        setItemQuantity={setItemQuantity}
        removeItem={removeItem}
        clearLoyaltyOnOrder={clearLoyaltyOnOrder}
        setLineRefundIdx={setLineRefundIdx}
        businessDateLoading={businessDateLoading}
        isSameBusinessDay={isSameBusinessDay}
        canAuthoriseFinance={canAuthoriseFinance}
        canEditPos={canEditPos}
        requestPriceOverride={requestPriceOverride}
        total={total}
        paid={paid}
        due={due}
      />

      {/* ------ Payment tabs ------ */}
      <PaymentSection
        order={order}
        branch={branch}
        isClosed={isClosed}
        total={total}
        due={due}
        paymentTabsMeta={PAYMENT_TABS}
        onlineProviders={ONLINE_PROVIDERS}
        onlineProviderLabel={ONLINE_PROVIDER_LABEL}
        closeOrder={closeOrder}
        seriesOptions={seriesOptions}
        seriesPanelOpen={seriesPanelOpen}
        setSeriesPanelOpen={setSeriesPanelOpen}
        payWithSeries={payWithSeries}
        activeCategories={activeCategories}
        paymentTab={paymentTab}
        setPaymentTab={setPaymentTab}
        paymentTypeId={paymentTypeId}
        setPaymentTypeId={setPaymentTypeId}
        typesForTab={typesForTab}
        onlineProvider={onlineProvider}
        setOnlineProvider={setOnlineProvider}
        isVoucherType={isVoucherType}
        voucherCode={voucherCode}
        setVoucherCode={setVoucherCode}
        voucherInfo={voucherInfo}
        setVoucherInfo={setVoucherInfo}
        voucherChecking={voucherChecking}
        searchVoucher={searchVoucher}
        paymentAmount={paymentAmount}
        setPaymentAmount={setPaymentAmount}
        addPayment={addPayment}
        removePayment={removePayment}
        onRefundPayment={(p) => setRefundPayment(p)}
        onWriteOffDeposit={(p) => { setWriteOffDeposit(p); setWriteOffReason(""); }}
        canManageRefunds={canManageRefunds}
        depositMode={depositMode}
      />

      {/* ------ Order Notes (closed orders only · internal, permanent) ------ */}
      {isClosed && (
        <OrderNotesCard orderId={order.id} refreshKey={order.status} />
      )}

      <OrderHistoryPanel
        orderId={order.id}
        refreshKey={`${order.status}|${order.updated_at || ""}|${(order.events || []).length}|${(order.payments || []).length}|${(order.refunds || []).length}`}
        sectionRef={historyRef}
      />

      <AddItemsDialog
        open={addItemsOpen}
        onOpenChange={setAddItemsOpen}
        products={products}
        services={services}
        series={series}
        giftTemplates={giftTemplates}
        packages={packages}
        branch={branch}
        staffList={orderStaffList}
        appointmentDate={appointmentDate}
        onAdd={(payload) => { addItem(payload); }}
      />
      <ApplySpecialDialog
        open={applySpecialOpen}
        onOpenChange={setApplySpecialOpen}
        specials={specials}
        current={order.special}
        onApply={applyInvoiceSpecial}
        onRemove={removeInvoiceSpecial}
      />
      <HistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} order={order} />
      <VoidDialog
        open={voidOpen}
        onOpenChange={setVoidOpen}
        order={order}
        branch={branch}
        onVoided={async () => { await refresh(); toast.success("Order voided"); setVoidOpen(false); }}
      />
      <RefundDialog
        open={refundOpen}
        onOpenChange={setRefundOpen}
        order={order}
        branch={branch}
        onRefunded={async () => { await refresh(); toast.success("Refund recorded"); setRefundOpen(false); }}
      />
      <LineItemRefundDialog
        open={lineRefundIdx !== null}
        onOpenChange={(o) => { if (!o) setLineRefundIdx(null); }}
        order={order}
        branch={branch}
        itemIndex={lineRefundIdx}
        onRefunded={async () => { await refresh(); toast.success("Line item refunded"); setLineRefundIdx(null); }}
      />

      <AlertDialog open={!!writeOffDeposit} onOpenChange={(v) => { if (!v && !writingOff) { setWriteOffDeposit(null); setWriteOffReason(""); } }}>
        <AlertDialogContent data-testid="writeoff-deposit-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Write off this deposit?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-muted-foreground mt-2 space-y-2">
                <div>
                  <span className="font-medium text-foreground">
                    {writeOffDeposit?.payment_type_name || "Deposit"}
                  </span>{" "}
                  · {formatBranchMoney(branch, writeOffDeposit?.amount || 0)}
                </div>
                <div className="text-xs">
                  Taken by {writeOffDeposit?.by_name || "staff"} on {fmtDateTime(writeOffDeposit?.at)}
                </div>
                <div className="text-xs">
                  Write-offs are permanent — the deposit will never convert to Treatment Sales. Use this for no-shows or clients who never return.
                </div>
                <div className="pt-2">
                  <label className="text-xs text-foreground block mb-1">Reason (required)</label>
                  <Input
                    value={writeOffReason}
                    onChange={(e) => setWriteOffReason(e.target.value)}
                    placeholder="e.g. No-show after 3 follow-ups"
                    disabled={writingOff}
                    data-testid="writeoff-reason-input"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="ghost" onClick={() => { setWriteOffDeposit(null); setWriteOffReason(""); }} disabled={writingOff} data-testid="writeoff-cancel">Cancel</Button>
            <AlertDialogAction
              className="bg-zinc-800 hover:bg-zinc-900 text-white"
              disabled={writingOff || !writeOffReason.trim()}
              data-testid="writeoff-confirm"
              onClick={async () => {
                if (!writeOffDeposit) return;
                setWritingOff(true);
                try {
                  await staffApi.post(
                    `/orders/${order.id}/payments/${writeOffDeposit.id}/write-off`,
                    { reason: writeOffReason.trim() },
                  );
                  await refresh();
                  toast.success("Deposit written off");
                  setWriteOffDeposit(null);
                  setWriteOffReason("");
                } catch (err) {
                  toast.error(formatApiError(err));
                } finally {
                  setWritingOff(false);
                }
              }}
            >
              {writingOff ? "Writing off…" : "Write off deposit"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!refundPayment} onOpenChange={(v) => { if (!v && !refundingPayment) setRefundPayment(null); }}>
        <AlertDialogContent data-testid="refund-payment-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Refund this payment?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-muted-foreground mt-2 space-y-1">
                <div>
                  <span className="font-medium text-foreground">
                    {refundPayment?.payment_type_name || "Payment"}
                  </span>{" "}
                  · {formatBranchMoney(branch, refundPayment?.amount || 0)}
                </div>
                <div className="text-xs">
                  Recorded by {refundPayment?.by_name || "staff"} on {fmtDateTime(refundPayment?.at)}
                  {refundPayment?.is_deposit ? " · Deposit" : ""}
                </div>
                <div className="text-xs mt-2">
                  The refund will be posted against the open order and the payment line stays in the audit trail. The order remains open until you click Close Order.
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="ghost" onClick={() => setRefundPayment(null)} disabled={refundingPayment} data-testid="refund-payment-cancel">Cancel</Button>
            <AlertDialogAction
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={handleRefundPayment}
              disabled={refundingPayment}
              data-testid="refund-payment-confirm"
            >
              {refundingPayment ? "Refunding…" : "Refund payment"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!paymentAlert} onOpenChange={(v) => { if (!v) setPaymentAlert(null); }}>
        <AlertDialogContent data-testid="payment-alert-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-rose-900">
              <span className="inline-block h-2 w-2 rounded-full bg-rose-500" /> Payment alert · {paymentAlert?.name}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-foreground mt-2 text-sm" data-testid="payment-alert-text">
                {(paymentAlert?.items || []).map((it, i) => (
                  <div
                    key={i}
                    className="whitespace-pre-wrap bg-rose-50 border border-rose-200 rounded-md p-3 mb-2 last:mb-0"
                    data-testid={`payment-alert-item-${i}`}
                  >
                    {it.title && <div className="font-medium mb-1">{it.title}</div>}
                    <div>{it.description}</div>
                  </div>
                ))}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction data-testid="payment-alert-ok">Got it</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

